import { Outlet, Link, useLocation } from "react-router-dom";
import { Plus, Home, LogOut, User, Sparkles, Crown, Zap, MessageCircle, Calendar, Bell, Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import ChatWidget from "@/components/ChatWidget";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const Layout = () => {
  const location = useLocation();
  const { user, logout, getPlanId, getPlanName } = useAuth();
  const planId = getPlanId();
  const planName = getPlanName();
  
  // Determinar si el usuario tiene plan Pro
  const isPro = planId === 'pro' || user?.subscription_plan === 'pro';

  const getPlanIcon = () => {
    switch (planId) {
      case 'pro':
        return <Crown className="w-3 h-3" />;
      case 'basic':
        return <Zap className="w-3 h-3" />;
      default:
        return <Sparkles className="w-3 h-3" />;
    }
  };

  const getPlanColor = () => {
    switch (planId) {
      case 'pro':
        return 'text-violet-600 bg-violet-50';
      case 'basic':
        return 'text-orange-600 bg-orange-50';
      default:
        return 'text-stone-600 bg-stone-100';
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-border/50">
        <div className="max-w-7xl mx-auto px-6 h-24 flex items-center justify-between">
          <Link 
            to="/" 
            className="flex items-center gap-3 group"
            data-testid="header-logo"
          >
            <img src="/logo-header.png" alt="Minut-IA" className="h-20 w-auto" />
          </Link>
          
          <nav className="flex items-center gap-2">
            <Link
              to="/"
              data-testid="nav-home"
              className={cn(
                "px-4 py-2 rounded-lg text-sm font-medium transition-all",
                location.pathname === "/"
                  ? "bg-foreground text-background"
                  : "hover:bg-secondary text-muted-foreground hover:text-foreground"
              )}
            >
              <Home className="w-4 h-4 inline mr-2" />
              Inicio
            </Link>
            <Link
              to="/chat"
              data-testid="nav-chat"
              className={cn(
                "px-4 py-2 rounded-lg text-sm font-medium transition-all",
                location.pathname === "/chat"
                  ? "bg-foreground text-background"
                  : "hover:bg-secondary text-muted-foreground hover:text-foreground"
              )}
            >
              <MessageCircle className="w-4 h-4 inline mr-2" />
              Chat-IA
            </Link>
            {isPro && (
              <Link
                to="/calendario"
                data-testid="nav-calendar"
                className={cn(
                  "px-4 py-2 rounded-lg text-sm font-medium transition-all",
                  location.pathname === "/calendario"
                    ? "bg-foreground text-background"
                    : "hover:bg-secondary text-muted-foreground hover:text-foreground"
                )}
              >
                <Calendar className="w-4 h-4 inline mr-2" />
                Calendario
              </Link>
            )}
            <Link
              to="/new"
              data-testid="nav-new-meeting"
              data-tour="new-meeting"
              className={cn(
                "px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2",
                location.pathname === "/new"
                  ? "bg-orange-500 text-white"
                  : "bg-orange-500 hover:bg-orange-600 text-white"
              )}
            >
              <Plus className="w-4 h-4" />
              Nueva Reunión
            </Link>
            
            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="ml-2 rounded-full" data-testid="user-menu">
                  <div className="w-9 h-9 rounded-full bg-stone-200 flex items-center justify-center hover:bg-stone-300 transition-colors">
                    <User className="w-4 h-4 text-stone-600" />
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56" sideOffset={8}>
                <div className="px-3 py-2 border-b border-border">
                  <p className="text-sm font-medium">{user?.name}</p>
                  <p className="text-xs text-muted-foreground">{user?.email}</p>
                  <div className={`mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${getPlanColor()}`}>
                    {getPlanIcon()}
                    Plan {planName}
                  </div>
                </div>
                <Link to="/pricing">
                  <DropdownMenuItem className="cursor-pointer m-1">
                    <Sparkles className="w-4 h-4 mr-2" />
                    {planId === 'free' ? 'Actualizar plan' : 'Ver planes'}
                  </DropdownMenuItem>
                </Link>
                <Link to="/notifications">
                  <DropdownMenuItem className="cursor-pointer m-1">
                    <Bell className="w-4 h-4 mr-2" />
                    Notificaciones
                  </DropdownMenuItem>
                </Link>
                <Link to="/recover">
                  <DropdownMenuItem className="cursor-pointer m-1">
                    <Search className="w-4 h-4 mr-2" />
                    Recuperar Grabaciones
                  </DropdownMenuItem>
                </Link>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={logout} 
                  className="text-red-600 cursor-pointer focus:text-red-600 focus:bg-red-50 m-1"
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Cerrar sesión
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>
        </div>
      </header>
      
      {/* Main Content */}
      <main className="flex-1">
        <Outlet />
      </main>
      
      {/* Footer */}
      <footer className="border-t border-border py-6 mt-auto">
        <div className="max-w-7xl mx-auto px-6 text-center text-sm text-muted-foreground">
          <p className="mb-2">Minut-IA — Transcribe y genera minutas con IA</p>
          <p className="text-xs">
            ¿Necesitas ayuda? Contáctanos en{" "}
            <a 
              href="mailto:soporte@minut-ia.com" 
              className="text-orange-500 hover:text-orange-600 underline"
            >
              soporte@minut-ia.com
            </a>
          </p>
        </div>
      </footer>

      {/* Chat Widget - only visible on protected routes */}
      <ChatWidget />
    </div>
  );
};

export default Layout;
